import React, {ReactNode, useEffect, useState} from 'react';
import {Box, Typography} from '@mui/material';
import {doc, onSnapshot} from 'firebase/firestore';
import {db} from '../firebase/firebase';
import ScheduifyWordmark from '../components/brand/ScheduifyWordmark';
import LoadingSpinner from '../components/global/LoadingSpinner';

type RuntimeState = {
  maintenanceEnabled: boolean;
  message: string;
};

type RuntimeMaintenanceGateProps = {
  children: ReactNode;
};

const unavailableState: RuntimeState = {
  maintenanceEnabled: true,
  message: 'Scheduify is temporarily unavailable while we complete an update.',
};

const parseRuntimeState = (value: unknown): RuntimeState => {
  if (!value || typeof value !== 'object') return unavailableState;
  const record = value as Record<string, unknown>;
  if (typeof record.maintenanceEnabled !== 'boolean') return unavailableState;
  return {
    maintenanceEnabled: record.maintenanceEnabled,
    message: typeof record.message === 'string' && record.message.trim()
      ? record.message.trim()
      : unavailableState.message,
  };
};

export const RuntimeMaintenanceGate: React.FC<RuntimeMaintenanceGateProps> = ({children}) => {
  const [state, setState] = useState<RuntimeState | null>(null);

  useEffect(() => {
    return onSnapshot(
      doc(db, 'system', 'runtime'),
      (snapshot) => setState(snapshot.exists() ? parseRuntimeState(snapshot.data()) : unavailableState),
      () => setState(unavailableState)
    );
  }, []);

  if (!state) {
    return (
      <LoadingSpinner fullScreen />
    );
  }

  if (state.maintenanceEnabled) {
    return (
      <Box sx={{
        minHeight: '100vh', display: 'grid', placeItems: 'center', p: 3,
        bgcolor: '#f7f7f5', color: '#111827', textAlign: 'center',
      }}>
        <Box sx={{maxWidth: 560}}>
          <ScheduifyWordmark />
          <Typography variant="h3" component="h1" sx={{mt: 4, mb: 2, fontWeight: 700}}>
            We’ll be right back
          </Typography>
          <Typography variant="h6" component="p" color="text.secondary">
            {state.message}
          </Typography>
        </Box>
      </Box>
    );
  }

  return <>{children}</>;
};
